import { validationContext, required, func } from 'validx'
import { FindMembers } from 'iam/MemberStore'
import { makeObservable, observable, action, computed } from 'mobx'
import { CursorPageQuery, CursorPaged } from 'types/definitions'
import { CancellationToken } from 'infra/cancellation'
import { roundToPrecision } from 'utils/number-util'
import User from 'iam/models/User'

/**
 * View model for a single allocation.
 */
export class AllocationInputViewModel {
  /**
   * The recipient (user getting paid).
   */
  recipient: User | null = null

  /**
   * The amount.
   */
  allocatedAmount = new AmountEditor(() => this.getPayoutTotal())

  /**
   * The authorized partial payout amount.
   */
  authorizedPartialPayoutAmount = new AmountEditor(
    () => this.allocatedAmount.value,
  )

  /**
   * Validation.
   */
  readonly validation = validationContext(this, {
    recipient: [required('Recipient is required')],
    allocatedAmount: [
      required('Amount is required'),
      func(
        () => this.allocatedAmount.value >= 0 || 'Amount must not be negative',
      ),
      func(
        () =>
          this.allocatedAmount.value <= this.getPayoutTotal() ||
          'Amount must not be greater than the total payout',
      ),
    ],
    authorizedPartialPayoutAmount: [
      required('Authorized partial payout amount is required'),
      func(
        () =>
          this.authorizedPartialPayoutAmount.value >= 0 ||
          'Amount must not be negative',
      ),
      func(
        () =>
          this.authorizedPartialPayoutAmount.value <=
            this.allocatedAmount.value ||
          'Authorized partial payout amount must not be greater than the allocated amount',
      ),
    ],
  })

  /**
   * Constructor.
   */
  constructor(
    private readonly workspaceId: string,
    private readonly getPayoutTotal: () => number,
    private readonly findMembers: FindMembers,
    private readonly onRemove: () => void,
    public readonly isAlreadySelected: (user: User) => boolean,
  ) {
    makeObservable(this, {
      recipient: observable,
      setRecipient: action.bound,
      remove: action.bound,
      validate: action.bound,
    })

    this.searchMembers = this.searchMembers.bind(this)
  }

  /**
   * Searches for members.
   */
  async searchMembers(
    searchText: string,
    pagination: CursorPageQuery,
    cancellationToken: CancellationToken,
  ) {
    const page = await this.findMembers(
      {
        search_text: searchText,
        after: pagination.after,
        limit: pagination.limit,
        workspace_id: this.workspaceId,
      },
      cancellationToken,
    )

    return CursorPaged.map(page, (d) => d.map((m) => m.user))
  }

  /**
   * Sets the recipient.
   */
  setRecipient(recipient: User | null) {
    this.recipient = recipient
  }

  /**
   * Asks to be removed from the list.
   */
  remove() {
    this.onRemove()
  }

  /**
   * Validates the allocation.
   */
  validate() {
    return this.validation.reset().validate().isValid
  }
}

/**
 * An editor for an amount that can be expressed as an absolute value
 * as well as a percentage value.
 */
export class AmountEditor {
  /**
   * The amount.
   */
  value = 0

  constructor(private readonly getTotal: () => number) {
    makeObservable(this, {
      value: observable,
      percentage: computed,
      setValue: action.bound,
      setPercentage: action.bound,
    })
  }

  /**
   * The percentage.
   */
  get percentage() {
    const total = this.getTotal()
    return roundToPrecision((this.value / total) * 100, 0)
  }

  /**
   * Sets the amount.
   */
  setValue(amount: number) {
    this.value = amount
  }

  /**
   * Sets the percentage.
   */
  setPercentage(percentage: number) {
    percentage = roundToPrecision(percentage, 0)

    this.value = roundToPrecision((this.getTotal() * percentage) / 100, 2)
  }
}
